<template>
  <div>
    <button
      v-if="!isModalOpen"
      ref="showDetailsButton"
      class="modal-button"
      @click="showDetails"
    >
      <img
        src="/Scripts/vue/tracking/img/ico_info.svg"
        :alt="$t('tracking.alt.info_picto')"
      />
      {{ $t("tracking.info.detailsButtonLabel") }}
    </button>
    <div
      v-if="isModalOpen"
      class="modal-parcelshop"
      @click.self="closeModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
    >
      <div class="modal-parcelshop__content">
        <div class="modal-parcelshop__header">
          <div class="modal-parcelshop__heading">
            <h2
              id="modalTitle"
              class="modal-parcelshop__title"
              :class="{ 'modal-parcelshop__title--amazon': details.isAmazonLocker }"
            >
              {{ details.locationName }}
            </h2>
            <p v-if="details.isAmazonLocker" class="modal-parcelshop__notice">
              {{ $t("tracking.info.modal.amazonLockerNotice") }}
            </p>
          </div>
          <button
            ref="closeButton"
            class="modal-parcelshop__close-button"
            @click="closeModal"
          >
            {{ $t("tracking.info.modal.closeButton") }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              class="close-icon"
            >
              <path
                d="M6 6L18 18"
                stroke="#96154A"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M18 6L6 18"
                stroke="#96154A"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
        <div class="parcelshop">
          <div class="parcelshop__image-wrapper">
            <img
              :src="imageUrl"
              class="parcelshop__image"
              alt="Image du point relais"
            />
          </div>
          <div class="parcelshop__info">
            <h3 class="parcelshop__details-title">
              {{ $t("tracking.info.modal.schedule") }}
              <span v-if="todaySchedule">
                <span
                  :class="{
                    'highlighted-time': isToday(new Date().getDay() - 1),
                  }"
                >
                  {{ formatSchedule(todaySchedule) }}
                </span>
                <span :class="{ open: isOpen, closed: !isOpen }">
                  ({{
                    isOpen
                      ? $t("tracking.info.modal.opened")
                      : $t("tracking.info.modal.closed")
                  }})
                </span>
              </span>
              <span v-else>
                {{ $t("tracking.info.modal.closed") }}
              </span>
            </h3>
            <ul class="parcelshop__schedule">
              <li
                v-for="(horaire, index) in details.horaires"
                :key="index"
                class="parcelshop__schedule-item"
              >
                <span
                  :class="[
                    'parcelshop__schedule-day',
                    { highlighted: isToday(index) },
                  ]"
                >
                  {{ getDayName(index) }}
                </span>
                <span
                  :class="[
                    'parcelshop__schedule-time',
                    { 'highlighted-time': isToday(index) },
                  ]"
                >
                  {{ formatSchedule(horaire) }}
                </span>
              </li>
            </ul>
            <p class="parcelshop__address">
              <strong>{{ $t("tracking.info.modal.address") }}</strong>
              {{ details.address }}
            </p>
            <button class="parcelshop__button" @click="redirectToMap">
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="location-dot-icon"
              >
                <g id="LocationDot">
                  <path
                    id="Vector"
                    d="M13.8609 23.4C16.2656 20.3906 21.75 13.0969 21.75 9C21.75 4.03125 17.7188 0 12.75 0C7.78125 0 3.75 4.03125 3.75 9C3.75 13.0969 9.23438 20.3906 11.6391 23.4C12.2156 24.1172 13.2844 24.1172 13.8609 23.4ZM12.75 6C13.5456 6 14.3087 6.31607 14.8713 6.87868C15.4339 7.44129 15.75 8.20435 15.75 9C15.75 9.79565 15.4339 10.5587 14.8713 11.1213C14.3087 11.6839 13.5456 12 12.75 12C11.9544 12 11.1913 11.6839 10.6287 11.1213C10.0661 10.5587 9.75 9.79565 9.75 9C9.75 8.20435 10.0661 7.44129 10.6287 6.87868C11.1913 6.31607 11.9544 6 12.75 6Z"
                    fill="white"
                  />
                </g>
              </svg>
              {{ $t("tracking.info.modal.showMapButton") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    details: Object,
  },
  data() {
    return {
      isModalOpen: false,
      daysOfWeek: [
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.1"),
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.2"),
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.3"),
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.4"),
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.5"),
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.6"),
        this.$t("tracking.parcelshopPicker.parcelshopDetails.weekDays.0"),
      ],
    };
  },
  mounted() {
    window.addEventListener("keydown", this.handleEscape);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleEscape);
  },
  computed: {
    imageUrl() {
      if (this.details) {
        const id = this.computeImageId(
          this.details.id || "",
          this.details.countryCode || ""
        );
        return `https://www.mondialrelay.fr/ww2/img/dynamique/pr.aspx?id=${id}`;
      }
      return "";
    },
    todaySchedule() {
      if (!this.details || !Array.isArray(this.details.horaires)) return null;
      const todayIndex = (new Date().getDay() + 6) % 7;
      return this.details.horaires[todayIndex] || null;
    },
    isOpen() {
      if (!this.todaySchedule) return false;

      const currentTime = new Date();
      const currentHour = currentTime.getHours();
      const currentMinutes = currentTime.getMinutes();

      const isWithinTime = (opening, closing) => {
        const [openHour, openMinute] = opening.split(":").map(Number);
        const [closeHour, closeMinute] = closing.split(":").map(Number);

        const openingTime = openHour * 60 + openMinute;
        const closingTime = closeHour * 60 + closeMinute;
        const currentTimeInMinutes = currentHour * 60 + currentMinutes;

        return (
          currentTimeInMinutes >= openingTime &&
          currentTimeInMinutes <= closingTime
        );
      };

      const openAM = this.todaySchedule.HeureOuvertureAM;
      const closeAM = this.todaySchedule.HeureFermetureAM;
      const openPM = this.todaySchedule.HeureOuverturePM;
      const closePM = this.todaySchedule.HeureFermeturePM;

      return (
        isWithinTime(openAM, closeAM) ||
        (openPM && closePM && isWithinTime(openPM, closePM))
      );
    },
  },
  methods: {
    showDetails() {
      this.isModalOpen = true;
      document.body.style.overflow = "hidden";

      this.$nextTick(() => {
        const modalContent = document.querySelector(
          ".modal-parcelshop__content"
        );
        if (modalContent) {
          modalContent.scrollTop = 0;
        }

        this.$refs.closeButton?.focus();
      });
    },

    handleEscape(e) {
      if (e.key === "Escape" && this.isModalOpen) this.closeModal();
    },

    closeModal() {
      this.isModalOpen = false;
      document.body.style.overflow = "";

      this.$nextTick(() => {
        this.$refs.showDetailsButton?.focus();
      });
    },

    computeImageId(id, countryCode) {
      let formatedId = id.toString();
      while (formatedId.length < 6) {
        formatedId = "0" + formatedId;
      }
      return countryCode + formatedId;
    },

    getDayName(index) {
      return this.daysOfWeek[index] || "";
    },

    isToday(index) {
      const todayIndex = (new Date().getDay() + 6) % 7;
      return index === todayIndex;
    },

    formatTime(time) {
      if (!time) return "";
      return time.slice(0, 5);
    },

    formatSchedule(horaire) {
      if (!horaire) return this.$t("tracking.info.closedText");
      const ranges = [];

      const formatRange = (start, end) =>
        start && end
          ? `${this.formatTime(start)} - ${this.formatTime(end)}`
          : null;
      const am = formatRange(
        horaire.HeureOuvertureAM,
        horaire.HeureFermetureAM
      );
      const pm = formatRange(
        horaire.HeureOuverturePM,
        horaire.HeureFermeturePM
      );

      if (am) ranges.push(am);
      if (pm) ranges.push(pm);

      return ranges.length > 0
        ? ranges.join(" / ")
        : this.$t("tracking.info.closedText");
    },

    redirectToMap() {
      const { geolocation, address } = this.details || {};
      let mapUrl = "";

      // Defaults are labels ("Non disponible"), so only numeric coordinates are used.
      const hasCoordinates =
        Number.isFinite(parseFloat(geolocation?.latitude)) &&
        Number.isFinite(parseFloat(geolocation?.longitude));

      if (hasCoordinates) {
        mapUrl = `https://www.google.com/maps?q=${geolocation.latitude},${geolocation.longitude}`;
      } else if (address) {
        mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`;
      }

      if (mapUrl) window.open(mapUrl, "_blank");
    },
  },
};
</script>

<style scoped lang="scss">
@import "./LockerDetails.scss";
</style>
